import React, { useState } from "react";
import { Box, Button, FormControl, FormLabel, FormErrorMessage, Input, Select, Checkbox, SimpleGrid } from "@chakra-ui/react";
import fetchWithToken from "views/auth/signIn/axiosInstance";
import { useTranslation } from "react-i18next";
import PAGE_IDS from "constants/pageIds";
import { GOLD_BORDER, GOLD_TEXT } from "constants/brandColors";
import TemplateContentEditor from "./TemplateContentEditor";

const BOT_TEMPLATES_PAGE_ID = PAGE_IDS.BOT_TEMPLATES;

const fieldProps = {
  bg: "white",
  borderColor: GOLD_BORDER,
  focusBorderColor: GOLD_BORDER,
};

function CreateBotTemplate({ onCreated }) {
  const { t } = useTranslation();
  const [key, setKey] = useState("");
  const [platform, setPlatform] = useState("");
  const [content, setContent] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const errors = {};
    if (!key.trim()) errors.key = t("Key is required");
    else if (!/^[a-z0-9_]+$/.test(key.trim())) errors.key = t("Lowercase letters, numbers, and underscores only");
    if (!content.trim()) errors.content = t("Content is required");
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }

    setIsSubmitting(true);
    try {
      const data = {
        page_id: BOT_TEMPLATES_PAGE_ID,
        key: key.trim(),
        platform: platform || null,
        content: content.trim(),
        isActive,
      };
      const response = await fetchWithToken(`${process.env.REACT_APP_NEWS_API_URL}/bot/template`, "POST", data);
      if (!response) {
        throw new Error("Failed to create template");
      }
      setKey("");
      setContent("");
      setPlatform("");
      setIsActive(true);
      onCreated?.();
    } catch (err) {
      setError(err.message || t("Failed to create template. Please try again later."));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FormControl>
      <form onSubmit={handleSubmit}>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          <FormControl isInvalid={!!fieldErrors.key}>
            <FormLabel fontSize="sm" fontWeight="500" color={GOLD_TEXT}>{t("Key")}</FormLabel>
            <Input
              isRequired
              placeholder="broadcast_default"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              fontFamily="mono"
              {...fieldProps}
            />
            <FormErrorMessage>{fieldErrors.key}</FormErrorMessage>
          </FormControl>
          <FormControl>
            <FormLabel fontSize="sm" fontWeight="500" color={GOLD_TEXT}>{t("Platform")}</FormLabel>
            <Select value={platform} onChange={(e) => setPlatform(e.target.value)} {...fieldProps}>
              <option value="">{t("Both")}</option>
              <option value="TELEGRAM">{t("Telegram")}</option>
              <option value="WHATSAPP">{t("WhatsApp")}</option>
            </Select>
          </FormControl>
        </SimpleGrid>

        <TemplateContentEditor
          value={content}
          onChange={setContent}
          isInvalid={!!fieldErrors.content}
          errorMessage={fieldErrors.content}
        />

        <Checkbox mt="16px" isChecked={isActive} onChange={(e) => setIsActive(e.target.checked)} colorScheme="brand">
          {t("Active")}
        </Checkbox>

        <Button
          type="submit"
          fontSize="sm"
          bg={GOLD_BORDER}
          color="#1A1A1A"
          _hover={{ bg: GOLD_TEXT, color: "white" }}
          _active={{ bg: GOLD_TEXT, color: "white" }}
          fontWeight="500"
          w="100%"
          h="50px"
          mt="20px"
          mb="8px"
          isLoading={isSubmitting}
        >
          {t("Create Template")}
        </Button>
        {error && (
          <Box color="red.500" mt={2} whiteSpace="pre-line">
            {error}
          </Box>
        )}
      </form>
    </FormControl>
  );
}

export default CreateBotTemplate;
